import { useState } from 'react';
import { CampaignCreationForm } from './campaignCreationForm';
import { CampaignCreationModalProps, isDuplicationProps } from './campaignCreationTypes';
import { ModalType } from '@copilot/common/store/models/redux/modal';
import { useCloseModal } from '@copilot/common/hooks/modal';
import { isEmpty, isNil } from 'lodash';
import { Modal } from 'antd';
import { useFeatureFlags } from '@copilot/common/hooks/useFeatureFlags/useFeatureFlags';
import { useSelector, useDispatch } from 'react-redux';
import { OrganizationMemberSelectors } from '@copilot/common/store/selectors/organizationMember';
import { OrganizationMember } from '@copilot/common/store/models/redux/organizationMember';
import { Ref } from 'redux-orm';
import {
    ICampaignFieldsFragment,
    ICampaignType,
    useCreateCampaignMutation,
    useDuplicateCampaignMutation,
} from '@copilot/data/graphql/_generated';
import { useNavigate } from 'react-router-dom';
import { CampaignType } from '@copilot/data/responses/interface';
import notificationManager from '@copilot/common/utils/notificationManager';
import { CampaignActions } from '@copilot/common/store/actions/campaign';
import { convertCampaignToReduxData } from './conversion';
import {
    CAMPAIGN_CREATE_SAVE_TRACKING_ID,
    CAMPAIGN_DUPLICATE_SAVE_TRACKING_ID,
} from '@copilot/common/tracking/userpilotEventConsts';
import { GraphQLFormattedError } from 'graphql';
/**
 * Modal for creating or duplicating a campaign
 * @param props
 */
export function CampaignCreationModal(props: CampaignCreationModalProps) {
    const { nameEditorValidator, width = 400, centered = true, ...rest } = props;
    const { isNewCampaignCreationEndpointEnabled } = useFeatureFlags();
    const [isLoadingOld, setIsLoadingOld] = useState<boolean>(false);

    //Duplication specific props
    const isDuplication = isDuplicationProps(props);
    const defaultName = isDuplication ? props.defaultName : '';
    const campaignId = isDuplication ? props.campaignId : '';
    const [duplicateCampaign, { loading: isDuplicatingCampaign }] = useDuplicateCampaignMutation();

    //Creation specific props
    const handleCreateCampaign = !isDuplication ? props.onCreate : undefined;
    const campaignType = !isDuplication ? props.campaignType : undefined;
    const [createCampaign, { loading: isCreatingCampaign }] = useCreateCampaignMutation();

    const closeModal = useCloseModal(ModalType.CampaignCreationModal);
    const activeMember = useSelector(OrganizationMemberSelectors.getActiveMember);
    const organizationMembers: readonly Ref<OrganizationMember>[] = useSelector(
        OrganizationMemberSelectors.getAllOrgMembers
    );
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isLoading = isLoadingOld || isCreatingCampaign || isDuplicatingCampaign;

    /**
     * Handler for creating the campaign using the old campaign creation endpoint
     * @param {string} name the name of the campaign
     */
    async function onCreateOld(name: string) {
        if (!isEmpty(name)) {
            setIsLoadingOld(true);
            await handleCreateCampaign?.(name);
            setIsLoadingOld(false);
            closeModal();
        }
    }

    /**
     * On form submit, create or duplicate the campaign based on the mode
     * @param {string} name the name of the campaign
     * @param {string} orgMemberId the id of the organization member to create the campaign for
     */
    async function onFormSubmit(name: string, orgMemberId?: string) {
        if (!isNewCampaignCreationEndpointEnabled && !isDuplication) {
            // Use the old campaign creation endpoint if the new one is not enabled and we're not duplicating
            await onCreateOld(name);
            return;
        }

        const isSelfCreate = orgMemberId === activeMember?.id || isNil(orgMemberId);
        const orgMemberIdToUse = isSelfCreate ? null : orgMemberId;
        try {
            let campaignData: ICampaignFieldsFragment | undefined | null;
            let operationErrors: readonly GraphQLFormattedError[] | undefined;

            if (isDuplication) {
                const { data, errors } = await duplicateCampaign({
                    variables: {
                        name,
                        campaignId,
                        orgMemberId: orgMemberIdToUse,
                    },
                });
                campaignData = data?.duplicateCampaign;
                operationErrors = errors;
            } else {
                const { data, errors } = await createCampaign({
                    variables: {
                        name,
                        type:
                            campaignType === CampaignType.Nurture
                                ? ICampaignType.Nurture
                                : ICampaignType.Prospecting,
                        orgMemberId: orgMemberIdToUse,
                    },
                });
                campaignData = data?.createCampaign;
                operationErrors = errors;
            }

            // Handle GraphQL errors if any
            if ((!isNil(operationErrors) && isEmpty(operationErrors)) || isNil(campaignData)) {
                console.error('GraphQL errors:', operationErrors);
                notificationManager.showErrorNotification({
                    message: `Failed to ${isDuplication ? 'duplicate' : 'create'} campaign: ${operationErrors?.[0]?.message ?? 'Unknown error'}`,
                });
                return;
            }

            notificationManager.showSuccessNotification({
                message: `${name} created successfully!`,
            });

            const memberForCampaign = isSelfCreate
                ? activeMember
                : organizationMembers.find((member) => member.id === orgMemberId);

            dispatch(
                CampaignActions.loadCampaigns([
                    convertCampaignToReduxData(campaignData, memberForCampaign),
                ])
            );

            navigate(`/campaign/${campaignData.id}`);
            closeModal();
        } catch (e: any) {
            console.error('Network or unexpected error:', e);
            notificationManager.showErrorNotification({
                message: `Failed to ${isDuplication ? 'duplicate' : 'create'} please refresh the page and try again.`,
            });
        }
    }

    /**
     * Get the member options for the campaign creation modal, if the organization members are not an array (eg not loaded yet), return an empty array
     */
    function getMemberOptions(): Array<{ label: string; value: string }> {
        return Array.isArray(organizationMembers)
            ? organizationMembers
                  .filter((member) => member.isActive)
                  .map((member: Ref<OrganizationMember>) => ({
                      label:
                          `${member.firstName} ${member.lastName}` +
                          (member.id === activeMember?.id ? ' (Me)' : ''),
                      value: member.id,
                  }))
            : [];
    }

    return (
        <Modal
            width={width}
            centered={centered}
            {...rest}
            title={
                isDuplication
                    ? 'Duplicate campaign'
                    : `Create ${campaignType === CampaignType.Nurture ? 'nurture' : 'prospecting'} campaign`
            }
            closable={!isLoading}
        >
            <CampaignCreationForm
                nameValidator={nameEditorValidator}
                onSubmit={(name, orgMemberId) => void onFormSubmit(name, orgMemberId)}
                isLoading={isLoading}
                memberOptions={getMemberOptions()}
                defaultName={defaultName}
                submitButtonText={isDuplication ? 'Duplicate' : 'Create'}
                submitTrackingID={
                    isDuplication
                        ? CAMPAIGN_DUPLICATE_SAVE_TRACKING_ID
                        : CAMPAIGN_CREATE_SAVE_TRACKING_ID
                }
            />
        </Modal>
    );
}
